// Honest per-mailbox health derived ONLY from that mailbox's own saved metadata.
// It never reads a global/last-run success flag, never infers that shared sync
// logs apply to this mailbox, and never triggers a sync. A bare recent
// `last_synced` timestamp is treated as "activity", NOT as proven success —
// success/failure is reported only when an explicit per-mailbox run result
// (`last_sync_status` / `last_sync_error`) is present on the record.
const NEUTRAL = 'bg-gray-100 text-gray-600';
const WARN = 'bg-amber-100 text-amber-800';
const ERROR = 'bg-red-100 text-red-800';
const OK = 'bg-green-100 text-green-800';

// The backend persists timestamps via datetime.utcnow().isoformat() — i.e. naive
// UTC with NO trailing "Z" and no offset. `new Date()` would parse that as the
// browser's LOCAL time, so a recent UTC sync looks like a future time in e.g.
// America/Chicago and gets flagged Unknown. Normalise known timezone-less ISO
// timestamps to UTC while preserving any that already carry Z or an explicit offset.
const parseTs = (ts) => {
  if (!ts) return null;
  let s = ts;
  if (typeof s === 'string') {
    const tIdx = s.indexOf('T');
    if (tIdx !== -1) {
      const time = s.slice(tIdx + 1);
      const hasZone = /[zZ]$/.test(time) || /[+-]\d\d:?\d\d$/.test(time);
      if (!hasZone) s = `${s}Z`;
    }
  }
  const ms = new Date(s).getTime();
  return Number.isNaN(ms) ? null : ms;
};

export function deriveMailboxHealth(mailbox, now = Date.now()) {
  if (mailbox.is_active === false) {
    return { label: 'Disabled', cls: NEUTRAL, title: 'Connection is inactive.' };
  }

  // Hard, mailbox-correlated evidence: the saved sign-in for THIS mailbox expired.
  const refreshExp = parseTs(mailbox.refresh_token_expires_at);
  if (refreshExp !== null && refreshExp < now) {
    return { label: 'Reconnect required', cls: ERROR, title: 'Saved sign-in for this mailbox has expired.' };
  }

  // Explicit per-mailbox run failure. `last_sync_status === 'error'` is authoritative
  // on its own and must be honoured even when no error-text field accompanies it.
  if (mailbox.last_sync_status === 'error' || mailbox.last_sync_error) {
    return {
      label: 'Last sync failed',
      cls: ERROR,
      title: mailbox.last_sync_error
        ? String(mailbox.last_sync_error)
        : 'The last recorded sync for this mailbox reported an error.',
    };
  }

  if (!mailbox.last_synced) {
    return { label: 'Not yet checked', cls: NEUTRAL, title: 'No sync has run for this mailbox yet.' };
  }

  // Validate the saved timestamp: invalid or impossible-future => Unknown, not healthy.
  const last = parseTs(mailbox.last_synced);
  if (last === null) {
    return { label: 'Unknown', cls: NEUTRAL, title: 'Saved last-check time is not a valid date.' };
  }
  const SKEW_MS = 5 * 60000;
  if (last > now + SKEW_MS) {
    return { label: 'Unknown', cls: NEUTRAL, title: 'Saved last-check time is in the future; treat as unverified.' };
  }

  const succeeded = mailbox.last_sync_status === 'success';

  if (mailbox.auto_sync_enabled) {
    const intervalMs = (mailbox.sync_interval_minutes || 60) * 60000;
    const staleAfter = Math.max(intervalMs * 3, 30 * 60000);
    if (now - last > staleAfter) {
      return { label: 'Stale', cls: WARN, title: 'Last check is older than the configured interval.' };
    }
    // Green only with explicit, recent, per-mailbox recorded success under active auto-sync.
    if (succeeded) {
      return { label: 'OK', cls: OK, title: 'Last recorded sync for this mailbox completed successfully.' };
    }
    return { label: 'Recent activity', cls: NEUTRAL, title: 'A sync ran recently; per-mailbox outcome is not recorded.' };
  }

  // Auto-sync off (manual / paused): descriptive and neutral, never "healthy" green —
  // even a recorded success here only describes a single past manual run.
  const intervalMs = (mailbox.sync_interval_minutes || 60) * 60000;
  const staleAfter = Math.max(intervalMs * 3, 30 * 60000);
  if (now - last > staleAfter) {
    return { label: 'Stale', cls: WARN, title: 'Auto-sync is off and the last recorded check is old.' };
  }
  return { label: 'Last recorded sync', cls: NEUTRAL, title: 'Auto-sync is off; showing the last recorded check only.' };
}
